import { markRaw } from 'vue';
import { clampZoom, clampViewport, zoomAt, screenToCanvas, resizeViewport } from '@/utils/canvasViewport';

const identity = () => ({ zoom: 1, x: 0, y: 0 });
const editingTarget = target => target?.closest?.('input, textarea, select, button, [contenteditable="true"]');

export default {
  props: {
    panMode: { type: Boolean, default: false },
    background: { type: Object, default: null },
    viewportKey: { type: [String, Number], default: null }
  },
  data() {
    return {
      viewport: identity(),
      spacePan: false,
      viewHovered: false,
      pointerPan: null,
      touchGesture: null,
      blockedTouches: false,
      touchCandidate: null,
      suppressTapUntil: 0,
      canvasBackgroundStyle: {}
    };
  },
  computed: {
    viewportLayerConfig() {
      const view = this.exportMode ? identity() : this.viewport;
      return { x: view.x, y: view.y, scaleX: view.zoom, scaleY: view.zoom };
    },
    viewportZoom() { return this.exportMode ? 1 : this.viewport.zoom; },
    navigationActive() { return Boolean(this.pointerPan || this.touchGesture || this.blockedTouches); },
    zoomedBackgroundStyle() {
      return { ...this.canvasBackgroundStyle,
        width: this.stageWidth + 'px', height: this.stageHeight + 'px',
        transform: `translate(${this.viewport.x}px, ${this.viewport.y}px) scale(${this.viewport.zoom})` };
    }
  },
  watch: {
    viewportKey() { this.resetViewport(); },
    background: {
      deep: true,
      handler() { this.$nextTick(() => this.refreshViewportBackground()); }
    },
    stageWidth(width, oldWidth) {
      this.$nextTick(() => {
        const oldHeight = oldWidth / this.aspectRatio;
        this.setViewport(resizeViewport(this.viewport, oldWidth, oldHeight, width, this.stageHeight));
      });
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleViewportKeyDown);
    window.addEventListener('keyup', this.handleViewportKeyUp);
    window.addEventListener('blur', this.cancelViewportNavigation);
    this.$nextTick(() => this.refreshViewportBackground());
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleViewportKeyDown);
    window.removeEventListener('keyup', this.handleViewportKeyUp);
    window.removeEventListener('blur', this.cancelViewportNavigation);
  },
  methods: {
    refreshViewportBackground() {
      const parent = this.$refs.editorContainer?.closest('.content-section');
      if (!parent) return;
      const style = getComputedStyle(parent);
      this.canvasBackgroundStyle = {
        backgroundColor: style.backgroundColor,
        backgroundImage: style.backgroundImage,
        backgroundSize: style.backgroundSize,
        backgroundPosition: style.backgroundPosition,
        backgroundRepeat: style.backgroundRepeat
      };
    },
    viewportEventPoint(event) {
      const rect = this.$refs.editorContainer.getBoundingClientRect();
      return { x: (event.clientX - rect.left) * this.stageWidth / rect.width,
        y: (event.clientY - rect.top) * this.stageHeight / rect.height };
    },
    viewportPointToDocument(point) {
      const local = screenToCanvas(point, this.viewport);
      return { x: local.x * this.canvasWidth / this.stageWidth,
        y: local.y * this.canvasHeight / this.stageHeight };
    },
    setViewport(view) {
      this.viewport = clampViewport(view, this.stageWidth, this.stageHeight);
      const layer = this.$refs.layer?.getNode();
      layer?.setAttrs(this.viewportLayerConfig);
      this.$refs.transformer?.getNode()?.forceUpdate();
      layer?.batchDraw();
      this.$emit('viewport-change', { ...this.viewport });
    },
    zoomBy(factor) {
      if (this.eraseDraft || this.navigationActive || this.$refs.transformer?.getNode()?.isTransforming()) return;
      const stage = this.$refs.stage?.getNode();
      if (stage?.find(node => node.isDragging()).length) return;
      this.refreshViewportBackground();
      this.setViewport(zoomAt(this.viewport, this.viewport.zoom * factor,
        { x: this.stageWidth / 2, y: this.stageHeight / 2 }, this.stageWidth, this.stageHeight));
    },
    resetViewport() {
      this.cancelEraseStroke?.();
      this.cancelViewportNavigation();
      this.setViewport(identity());
    },
    handleViewportWheel(event) {
      if (this.eraseDraft || this.exportMode || this.navigationActive || this.$refs.transformer?.getNode()?.isTransforming()) return;
      const stage = this.$refs.stage?.getNode();
      if (stage?.find(node => node.isDragging()).length) return;
      this.refreshViewportBackground();
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.stageHeight : 1);
      this.setViewport(zoomAt(this.viewport,
        this.viewport.zoom * Math.exp(-Math.max(-150, Math.min(150, delta)) * 0.003),
        this.viewportEventPoint(event), this.stageWidth, this.stageHeight));
    },
    shouldBlockInteraction() {
      return Boolean(this.exportMode || this.panMode || this.spacePan || this.navigationActive || this.eraserEnabled);
    },
    shouldBlockTap() {
      return this.shouldBlockInteraction() || Date.now() < this.suppressTapUntil;
    },
    guardViewportClick(event) {
      if (!this.shouldBlockTap()) return;
      event.preventDefault();
      event.stopPropagation();
    },
    guardViewportMouseDown(event) {
      if (!(this.pointerPan || this.panMode || this.spacePan || this.eraserEnabled || event.button === 1)) return;
      event.preventDefault();
      event.stopPropagation();
    },
    handleViewportKeyDown(event) {
      if (editingTarget(event.target) || this.exportMode) return;
      const focused = this.$refs.editorContainer?.contains(document.activeElement);
      if (!(this.viewHovered || focused)) return;
      if (event.key === 'Escape') {
        this.cancelEraseStroke?.();
        this.cancelViewportNavigation();
        this.$emit('exit-pan');
        this.$emit('exit-eraser');
      } else if (event.code === 'Space') {
        if (this.eraseDraft) return;
        const stage = this.$refs.stage?.getNode();
        if (this.$refs.transformer?.getNode()?.isTransforming() || stage?.find(node => node.isDragging()).length) return;
        event.preventDefault();
        this.spacePan = true;
      } else if (focused && ['+', '=', '-', '0'].includes(event.key)) {
        event.preventDefault();
        if (event.key === '0') this.resetViewport();
        else this.zoomBy(event.key === '-' ? 0.8 : 1.25);
      }
    },
    handleViewportKeyUp(event) {
      if (event.code === 'Space') this.spacePan = false;
    },
    handleViewportPointerDown(event) {
      if (event.pointerType === 'touch' || this.exportMode) return;
      if (!(event.button === 1 || (event.button === 0 && (this.panMode || this.spacePan)))) return;
      event.preventDefault();
      event.stopPropagation();
      this.refreshViewportBackground();
      this.pointerPan = { id: event.pointerId, start: this.viewportEventPoint(event), view: { ...this.viewport } };
      this.$refs.editorContainer.setPointerCapture(event.pointerId);
      this.$refs.editorContainer.focus({ preventScroll: true });
    },
    handleViewportPointerMove(event) {
      const pan = this.pointerPan;
      if (!pan || pan.id !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      const point = this.viewportEventPoint(event);
      this.setViewport({ ...pan.view, x: pan.view.x + point.x - pan.start.x, y: pan.view.y + point.y - pan.start.y });
    },
    handleViewportPointerUp(event) {
      if (!this.pointerPan || this.pointerPan.id !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      this.pointerPan = null;
      this.suppressTapUntil = Date.now() + 300;
      if (this.$refs.editorContainer.hasPointerCapture(event.pointerId)) {
        this.$refs.editorContainer.releasePointerCapture(event.pointerId);
      }
    },
    rememberTouchObject(point) {
      const stage = this.$refs.stage?.getNode();
      let node = stage?.getIntersection(point);
      if (!node) { this.touchCandidate = null; return; }
      const pendingTarget = markRaw(node);
      if (node.name() === 'perspective-handle' || node.getParent()?.getClassName() === 'Transformer') {
        node = this.getSelectedKonvaNode();
      } else {
        while (node?.getParent() && node.getParent().getClassName() !== 'Layer') node = node.getParent();
      }
      if (!node || node === stage) return;
      const attrs = {};
      for (const key of ['x', 'y', 'width', 'height', 'scaleX', 'scaleY', 'rotation', 'offsetX', 'offsetY']) attrs[key] = node[key]();
      this.touchCandidate = { node: markRaw(node), pendingTarget, attrs };
    },
    stopObjectGesture() {
      // Mark navigation active before stopping Konva, because stopDrag and
      // stopTransform emit their end events. Those must not save an accidental edit.
      this.isDrawingLasso = false;
      this.lassoPoints = [];
      this.draftPerspective = null;
      this.cancelEraseStroke?.();
      const stage = this.$refs.stage?.getNode();
      stage?.find(node => node.isDragging()).forEach(node => node.stopDrag());
      // Konva queues a "ready" drag on touchstart even before the pointer has
      // moved. isDragging() misses it; stopDrag also flushes that pending entry.
      this.touchCandidate?.pendingTarget?.stopDrag();
      this.touchCandidate?.node.stopDrag();
      stage?.stopDrag?.();
      this.$refs.transformer?.getNode()?.stopTransform();
      if (this.touchCandidate?.node.getStage()) {
        this.touchCandidate.node.setAttrs(this.touchCandidate.attrs);
      }
      this.$refs.transformer?.getNode()?.forceUpdate();
    },
    touchMetrics(touches) {
      const a = this.viewportEventPoint(touches[0]), b = this.viewportEventPoint(touches[1]);
      return { center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        distance: Math.hypot(a.x - b.x, a.y - b.y) };
    },
    blockViewportTouch(event) {
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();
    },
    handleViewportTouchStart(event) {
      if (this.exportMode) return;
      if (event.touches.length >= 2) {
        this.blockViewportTouch(event);
        const metrics = this.touchMetrics(event.touches);
        this.blockedTouches = true;
        this.stopObjectGesture();
        this.refreshViewportBackground();
        this.touchGesture = { ...metrics, view: { ...this.viewport },
          anchor: screenToCanvas(metrics.center, this.viewport) };
      } else if (this.blockedTouches) {
        this.blockViewportTouch(event);
      } else if (this.panMode) {
        this.blockViewportTouch(event);
        this.blockedTouches = true;
        const point = this.viewportEventPoint(event.touches[0]);
        this.touchGesture = { single: true, center: point, view: { ...this.viewport } };
        this.refreshViewportBackground();
      } else if (this.eraserEnabled) {
        this.blockViewportTouch(event);
      } else {
        this.rememberTouchObject(this.viewportEventPoint(event.touches[0]));
      }
    },
    handleViewportTouchMove(event) {
      if (!this.blockedTouches && this.eraserEnabled) { this.blockViewportTouch(event); return; }
      if (!this.blockedTouches) return;
      this.blockViewportTouch(event);
      const gesture = this.touchGesture;
      if (!gesture) return;
      if (event.touches.length >= 2 && !gesture.single) {
        const metrics = this.touchMetrics(event.touches);
        const zoom = clampZoom(gesture.view.zoom * metrics.distance / Math.max(1, gesture.distance));
        this.setViewport({ zoom, x: metrics.center.x - gesture.anchor.x * zoom,
          y: metrics.center.y - gesture.anchor.y * zoom });
      } else if (gesture.single && event.touches.length === 1) {
        const point = this.viewportEventPoint(event.touches[0]);
        this.setViewport({ ...gesture.view, x: gesture.view.x + point.x - gesture.center.x,
          y: gesture.view.y + point.y - gesture.center.y });
      }
    },
    handleViewportTouchEnd(event) {
      if (!this.blockedTouches && this.eraserEnabled) { this.blockViewportTouch(event); return; }
      if (!this.blockedTouches) { this.touchCandidate = null; return; }
      this.blockViewportTouch(event);
      if (event.touches.length < 2 && !this.touchGesture?.single) this.touchGesture = null;
      if (!event.touches.length) {
        this.touchGesture = null;
        this.blockedTouches = false;
        this.touchCandidate = null;
        this.suppressTapUntil = Date.now() + 300;
      }
    },
    cancelViewportNavigation() {
      this.cancelEraseStroke?.();
      const id = this.pointerPan?.id;
      const element = this.$refs.editorContainer;
      if (id != null && element?.hasPointerCapture(id)) element.releasePointerCapture(id);
      this.pointerPan = null;
      this.touchGesture = null;
      this.blockedTouches = false;
      this.touchCandidate = null;
      this.spacePan = false;
    }
  }
};